import { computed, ref } from 'vue';
import type { Router } from 'vue-router';
import { formatDateForApi } from '@/shared/lib';
import { trainingApi, type TrainingDetail, type UpdateHomologationTrainingPayload } from '@/entities/Training';
import { useOnboardingMinistrantes, type OnboardingMinistranteRow } from './useOnboardingMinistrantes';
import { useParticipantSelection } from './useParticipantSelection';
import { useTrainingFormSubmit } from './useTrainingFormSubmit';
import { buildMinistranteSessionPayload } from '../lib/buildMinistranteSessionPayload';
import { buildMemberOptions } from '../lib/buildMemberOptions';

export interface UseEditHomologationTrainingEditContext {
  trainingId: number;
  initial: TrainingDetail;
  /**
   * Captured synchronously by the edit dispatcher before its await, since this composable
   * is instantiated after one — useRouter() would silently resolve to undefined by then.
   */
  router?: Router;
}

function isEmptyMinistranteRow(row: OnboardingMinistranteRow): boolean {
  return row.responsibleMemberId === null && row.responsibleUserPositionId === null && row.trainingDate === null;
}

/** A row is valid either untouched (no new ministrante here) or fully filled — never half-filled. */
function isMinistranteRowValid(row: OnboardingMinistranteRow): boolean {
  return isEmptyMinistranteRow(row) || buildMinistranteSessionPayload(row) !== null;
}

/**
 * SGQ Fluxo de Homologação has no manual creation form — the training is
 * generated by the document approval flow. Documento/Revisão/Ministrante(s)
 * já registrado(s)/Responsável pela aprovação stay locked (AC06); this only
 * edits Observações plus the genuinely new capability the Figma design adds
 * on top: selecting collaborators and adding (never editing/removing)
 * ministrante sessions.
 */
export function useEditHomologationTraining(editContext: UseEditHomologationTrainingEditContext) {
  const { saving, submit: submitTraining } = useTrainingFormSubmit(editContext.router);

  const notes = ref(editContext.initial.notes ?? '');

  const {
    members,
    loading,
    loaded,
    collaborators,
    visibleMemberIds,
    load: loadParticipants,
    initializeParticipants,
  } = useParticipantSelection();

  const newMinistrantes = useOnboardingMinistrantes(members);
  const memberOptions = computed(() => buildMemberOptions(members.value));

  // EditHomologationTrainingForm.vue also calls load() from its own onMounted, but
  // useEditTraining.ts's dispatcher already awaited load() once before that component
  // mounts. Without this guard, the redundant call would re-run initializeParticipants's
  // network fetch for no reason.
  let participantsInitialized = false;

  async function load(): Promise<void> {
    await loadParticipants();
    if (!participantsInitialized) {
      participantsInitialized = true;
      await initializeParticipants(
        editContext.initial.positions.map((position) => position.id),
        editContext.initial.trainees.map((trainee) => trainee.memberId)
      );
    }
  }

  const canSave = computed(() => newMinistrantes.rows.value.every(isMinistranteRowValid));

  function buildSubmitPayload(): UpdateHomologationTrainingPayload | null {
    const newSessions: NonNullable<UpdateHomologationTrainingPayload['newSessions']> = [];
    for (const row of newMinistrantes.rows.value) {
      if (isEmptyMinistranteRow(row)) continue;
      const session = buildMinistranteSessionPayload(row);
      if (session === null) return null;
      newSessions.push(session);
    }

    const participantMemberIds = visibleMemberIds.value.map(Number);
    if (participantMemberIds.some((id) => Number.isNaN(id))) return null;

    const positionIds = collaborators.selectedPositions.value.map(Number);
    if (positionIds.some((id) => Number.isNaN(id))) return null;

    return {
      notes: notes.value.trim() ? notes.value.trim() : undefined,
      participantMemberIds,
      positionIds,
      ...(newSessions.length > 0 ? { newSessions } : {}),
    };
  }

  async function submit(onSuccess?: () => void): Promise<void> {
    await submitTraining(
      canSave.value,
      buildSubmitPayload,
      (payload) => trainingApi.updateHomologationTraining(editContext.trainingId, payload),
      { mode: 'edit', onSuccess }
    );
  }

  const snapshot = computed(() =>
    JSON.stringify({
      notes: notes.value.trim(),
      participants: [...visibleMemberIds.value].sort(),
      positions: [...collaborators.selectedPositions.value].sort(),
      newMinistrantes: newMinistrantes.rows.value.map((row) => ({
        responsibleMemberId: row.responsibleMemberId,
        responsibleUserPositionId: row.responsibleUserPositionId,
        trainingDate: row.trainingDate ? formatDateForApi(row.trainingDate) : null,
      })),
    })
  );

  return {
    notes,
    members,
    memberOptions,
    loading,
    loaded,
    collaborators,
    newMinistrantes,
    saving,
    canSave,
    snapshot,
    load,
    submit,
  };
}
